import { Client } from "@/Common/Api/Client";
import { ErrorMessage } from "@/Common/Api/Dto/ErrorMessage";
import { ExternalBillingUserInfoDto } from "@/Common/Api/ExternalBilling/Dto/ExternalBillingUserInfoDto";
import { ExternalBillingUserInfoResponse } from "@/Common/Api/ExternalBilling/Dto/Responses/ExternalBillingUserInfoResponse";
import { BaseReactComponent } from "@/Common/Components/BaseReactComponent";
import * as ServerOperations from "@/Common/Operations/operations.server";
import { BillingProvider, BillingProviderHelper } from "@/Common/Services/ExternalBilling/BillingProvider";

interface IExternalBillingCredentialsPopupProps {
    billingProvider: BillingProvider;
    externalBillingUser: ExternalBillingUserInfoDto;
    onClose: () => void;
    onAuthSuccess?: (billingUser: ExternalBillingUserInfoDto) => void;
}

class ExternalBillingCredentialsPopupState {
    public username: string;
    public password: string;
    public formattedErrors: Partial<ErrorMessage>[];
    public isSubmitting: boolean;
    public isUsernameInvalid: boolean;
    public isPasswordInvalid: boolean;
}

class ExternalBillingCredentialsPopup extends BaseReactComponent<IExternalBillingCredentialsPopupProps, ExternalBillingCredentialsPopupState> {
    private isSubmitting = false;

    public constructor(props: IExternalBillingCredentialsPopupProps) {
        super(props);
        this.state = {
            username: this.props.externalBillingUser?.BillingUsername ?? "",
            password: "",
            formattedErrors: [],
            isSubmitting: false,
            isUsernameInvalid: false,
            isPasswordInvalid: false
        };
    }

    public componentDidMount(): void {
        this.setOverlay("ExternalBillingCredentialsPopup");
    }

    public render(): JSX.Element {
        const saveDisabled =
            this.state.isSubmitting
            || this.state.isUsernameInvalid
            || IsNullOrWhiteSpace(this.state.username)
            || this.state.isPasswordInvalid
            || IsNullOrWhiteSpace(this.state.password);

        const resetPasswordUrl = cacheStore.ClinicSettingsServices.ExternalBillingPasswordResetUrls[BillingProvider[this.props.billingProvider]];

        return (
            <form className="dsq-nip-popup auto" onKeyDown={e => this.handleKeyDown(e)}>
                <div className="dsn-header p-header">
                    <span>{cacheStore.GetString("LoginLabel")}: {BillingProviderHelper.getProviderDisplayName(this.props.billingProvider)}</span>
                </div>

                <div className="dsn-main">
                    <div style={{ paddingRight: "20px" }}>
                        <label className={this.state.isUsernameInvalid ? "invalid" : ""} htmlFor="externalBillingLoginUsername">{cacheStore.GetString("UserNameLabel")}</label>
                        <input
                            id="externalBillingLoginUsername"
                            className={`form-control${(this.state.isUsernameInvalid ? " invalid" : "")}`}
                            type="text"
                            onChange={(e: any) => this.handleUsernameChanged(e.target.value)}
                            value={this.state.username}
                            autoComplete="off"
                        />
                    </div>
                    <div style={{ marginTop: "10px", paddingRight: "20px" }}>
                        <label className={this.state.isPasswordInvalid ? "invalid" : ""} htmlFor="externalBillingLoginPassword">{cacheStore.GetString("PasswordLabel")}</label>
                        <input
                            id="externalBillingLoginPassword"
                            className={`form-control${(this.state.isPasswordInvalid ? " invalid" : "")}`}
                            type="password"
                            onChange={(e: any) => this.handlePasswordChanged(e.target.value)}
                            value={this.state.password}
                            autoComplete="off"
                        />
                    </div>
                    {this.state.formattedErrors.any() && (
                        <div style={{ marginTop: "10px", paddingRight: "20px" }}>
                            <ul>
                                {this.state.formattedErrors.map((error, i) =>
                                    (error.Code === "Locked" && !IsNullOrWhiteSpace(resetPasswordUrl)) ?
                                        <li key={`${i}${error.Message}`}>{error.Message} <a href={resetPasswordUrl} target="_blank">{resetPasswordUrl}</a></li> :
                                        <li key={`${i}${error.Message}`}>{error.Message}</li>

                                )}
                            </ul>
                        </div>
                    )}
                </div>

                <div className="dsn-footer" style={{ "marginTop": "10px" }}>
                    <div className="flex-container" style={{ "justifyContent": "flex-end" }}>
                        <button
                            className="btn btn-default push-right-5"
                            onClick={() => this.props.onClose()}
                        >
                            {cacheStore.GetString("CancelLabel")}
                        </button>
                        <button
                            className="btn btn-primary"
                            disabled={saveDisabled}
                            onClick={() => this.onSubmitAsync()}
                        >
                            {cacheStore.GetString("LoginLabel")}
                        </button>
                    </div>
                </div>
            </form>
        );
    }

    private handleUsernameChanged(text: string): void {
        this.mutateState(s => {
            s.username = text;
            s.isUsernameInvalid = IsNullOrWhiteSpace(text);
        });
    }

    private handlePasswordChanged(text: string): void {
        this.mutateState(s => {
            s.password = text;
            s.isPasswordInvalid = IsNullOrWhiteSpace(text);
        });
    }

    private handleKeyDown(e: React.KeyboardEvent): void {
        // enter
        if (e.keyCode === 13)
            this.onSubmitAsync();

        // escape
        if (e.keyCode === 27)
            this.props.onClose();
    }

    public isUsernameValid(typedUsername: string, badUsername: string): boolean {
        const mustChangeUsername = !IsNullOrUndefined(badUsername) && badUsername == typedUsername;

        return !IsNullOrUndefined(typedUsername) && !mustChangeUsername;
    }

    private async onSubmitAsync(): Promise<void> {
        if (this.isSubmitting || this.state.isUsernameInvalid || this.state.isPasswordInvalid)
            return;

        this.isSubmitting = true;

        this.mutateState(
            s => s.isSubmitting = true
        );

        let formattedErrors: Partial<ErrorMessage>[] = [];

        try {
            const response = await Client.postAsync<ExternalBillingUserInfoResponse>(
                ServerOperations.Server.ExternalBilling_SaveUserInfo,
                {
                    BillingProvider: this.props.billingProvider,
                    BillingUsername: this.state.username,
                    BillingPassword: this.state.password
                }
            );

            if (response.isSuccess) {
                const userInfo = response.data.BillingUserInfo;

                sessionStore.getCurrentEmployee().setExternalBillingUser(userInfo);
                if (userInfo.BillingAuthenticationIsValid) {
                    this.props.onAuthSuccess?.(userInfo);
                    this.props.onClose();
                }
            }
            else if (response.errors?.length) {
                formattedErrors = response.errors.map(message =>
                    ({
                        Message: message.VocabularyKey
                            ? cacheStore.GetString(message.VocabularyKey)
                            : message.Message,
                        Code: message.Code

                    }));
            }
        }
        catch {
            // do nothing - treat error as login fail
        }

        this.mutateState(
            s => {
                s.formattedErrors = formattedErrors;
                s.isSubmitting = false;
            },
            () => this.isSubmitting = false
        );
    }
}

export { ExternalBillingCredentialsPopup };
